import { useState } from 'react';
import { api } from '../../api';
import type { LicensedDomain, OrganizationLicenceView } from '../../api';
import { useResource } from '../../hooks';
import { dataEntityLabel, licenceReasonText } from '../../labels';
import { Callout, Section } from '../primitives';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import { HelpTip } from '../primitives/HelpTip';
import type { Column } from '../primitives/ResponsiveTable';
import { ConfirmDialog, ErrorNotice, SkeletonPage, StatusBadge } from '../ui';
import { statusFor } from '../../labels';

const dayFormat: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
const momentFormat: Intl.DateTimeFormatOptions = { ...dayFormat, hour: 'numeric', minute: '2-digit' };
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, dayFormat);
const moment = (iso: string) => new Date(iso).toLocaleString(undefined, momentFormat);

/**
 * The organization's plan as its store extensions see it (CO-143): first
 * what needs attention, then the plan, its use and who pays for it in one
 * row (CO-160), then the production domains counted against the plan's
 * limit. Owners release a domain to make room for another.
 */
export function LicencePanel({ isOwner }: { isOwner: boolean }) {
  const licence = useResource(() => api.licence(), [], undefined, { key: 'licence' });
  const [view, setView] = useState<OrganizationLicenceView | null>(null);
  const [releasing, setReleasing] = useState<LicensedDomain | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const data = view ?? licence.data;

  async function release(domain: LicensedDomain) {
    setBusy(true);
    setError(null);
    try {
      setView(await api.releaseLicensedDomain(domain.id));
      setReleasing(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<LicensedDomain>[] = [
    {
      id: 'domain',
      header: 'Domain',
      card: 'title',
      width: 'fill',
      cell: (d) => (d.host ? <span translate="no">{d.host}</span> : <span className="muted">No public address reported</span>),
    },
    {
      id: 'project',
      header: 'Project',
      width: 28,
      cell: (d) => (
        <span className="cell-stack">
          <span>{d.project_name || 'Project'}</span>
          <span className="cell-stack__secondary">{slotPlace(d)}</span>
        </span>
      ),
    },
    { id: 'status', header: 'Status', card: 'status', width: 'status', cell: (d) => <StatusBadge kind="licensedDomain" value={d.status} /> },
    {
      id: 'first-seen',
      header: 'First seen',
      width: 'time',
      priority: 'secondary',
      cell: (d) => <time dateTime={d.first_seen_at}>{day(d.first_seen_at)}</time>,
    },
  ];

  return (
    <Section
      title="Plan"
      testId="licence-panel"
    >
      {licence.loading && !data ? <SkeletonPage cards={0} rows={3} /> : null}
      <ErrorNotice error={licence.error ?? error} onRetry={licence.error ? licence.reload : undefined} />
      {data ? <LicenceSummary view={data} /> : null}
      {data && data.domains.length > 0 ? (
        <ResponsiveTable
          rows={data.domains}
          rowKey={(d) => d.id}
          caption="Licensed production domains"
          className="licence-domains"
          testId="licensed-domains"
          columns={columns}
          actionsSize="md"
          actions={
            isOwner
              ? (d) => (
                  <button className="button button--secondary button--small" type="button" onClick={() => setReleasing(d)} aria-label={`Release ${slotName(d)}`}>
                    Release
                  </button>
                )
              : undefined
          }
        />
      ) : null}
      {data && data.licence && data.domains.length === 0 ? (
        <p className="muted">No production store has checked in</p>
      ) : null}
      <ConfirmDialog
        open={releasing !== null}
        title={releasing ? `Release ${slotName(releasing)}?` : 'Release the domain?'}
        description="The shop stops counting against the plan, and a shop waiting for a free slot takes its place. A shop that still runs takes a slot back at its next check when one is free; until then it sends no data and applies no ERP updates. The shop itself keeps working."
        confirmLabel="Release domain"
        busy={busy}
        onConfirm={() => releasing && void release(releasing)}
        onCancel={() => setReleasing(null)}
      />
    </Section>
  );
}

/** Where a slot lives: its environment and, for a store with websites, the website. */
function slotPlace(d: LicensedDomain): string {
  const env = d.environment_name || 'Production';
  return d.website_code ? `${env}, website ${d.website_code}` : env;
}

/** How a slot is named in actions: its hostname, or its website when it has none. */
function slotName(d: LicensedDomain): string {
  if (d.host) return d.host;
  return d.website_code ? `website ${d.website_code} of ${d.project_name || 'the project'}` : d.project_name || 'this shop';
}

/**
 * The one answer to "may our store extensions use the product": the plan's
 * state (CO-184, OR13). It is first on the Billing tab; the subscription
 * below it and the plan's details here are the evidence for it. The licence
 * already folds the subscription in for a plan that is paid by one (it
 * lapses when the subscription does), so no second "entitled" word is shown.
 */
function EntitlementConclusion({ view }: { view: OrganizationLicenceView }) {
  const reason = view.reason ? licenceReasonText[view.reason] ?? '' : '';
  if (view.status === 'none') {
    return (
      <Callout tone="info" title="No plan yet" testId="entitlement-conclusion">
        Hestaworks assigns the plan; until then your store extensions send nothing.
      </Callout>
    );
  }
  if (view.status === 'grace') {
    return (
      <Callout tone="warning" title="Plan in grace period" testId="entitlement-conclusion">
        {reason} Your stores keep working until{' '}
        {view.grace_until ? <time dateTime={view.grace_until}>{moment(view.grace_until)}</time> : 'the grace period ends'}.
        After that they stop sending data and applying ERP updates; the shops themselves keep running.
      </Callout>
    );
  }
  if (view.status === 'expired') {
    return (
      <Callout tone="danger" title="Not licensed" testId="entitlement-conclusion">
        {reason} Your store extensions send nothing and apply no ERP updates. The shops themselves keep running.
      </Callout>
    );
  }
  // An active plan is not announced: its Status row in the facts says it.
  if (view.status === 'active' && view.plan) return null;
  // A state this screen has no sentence for is said as it is, never as fine.
  return (
    <Callout tone="info" title={statusFor('licence', view.status).label} testId="entitlement-conclusion">
      {reason || 'The plan’s state is reported, but this page has no explanation for it.'}
    </Callout>
  );
}

function LicenceSummary({ view }: { view: OrganizationLicenceView }) {
  const used = view.domains.filter((d) => d.status === 'active').length;
  const waiting = view.domains.filter((d) => d.status === 'pending').length;
  return (
    <>
      <EntitlementConclusion view={view} />
      {view.plan ? (
        <dl className="settings-facts" data-testid="licence-facts">
          <div>
            <dt>Plan</dt>
            <dd>{view.plan.name}</dd>
          </div>
          {view.status === 'active' ? (
            <div>
              <dt>Status</dt>
              <dd data-testid="licence-status">
                <StatusBadge kind="licence" value="active" />
              </dd>
            </div>
          ) : null}
          <div>
            <dt>
              Production domains
              <HelpTip label="What counts as a production domain">One domain per production shop website.</HelpTip>
            </dt>
            <dd>
              {used} of {view.domain_limit} in use
              {waiting > 0 ? <span className="muted">, {waiting} waiting for a free slot</span> : null}
            </dd>
          </div>
          <div>
            <dt>Paid by</dt>
            <dd data-testid="licence-source">{view.licence?.source === 'stripe' ? 'Subscription' : 'Assigned by Hestaworks'}</dd>
          </div>
          {view.licence?.source === 'manual' && view.licence.manual_valid_until ? (
            <div>
              <dt>Valid until</dt>
              <dd>
                <time dateTime={view.licence.manual_valid_until}>{day(view.licence.manual_valid_until)}</time>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Stores send</dt>
            <dd>{view.entities.length > 0 ? view.entities.map((e) => dataEntityLabel[e] ?? e).join(', ') : 'Nothing'}</dd>
          </div>
        </dl>
      ) : null}
    </>
  );
}
